//
//  SDKBorderToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { ALL_BORDER_POSITIONS, BorderPosition, } from "../enums/SDKBorderPosition";
import { ALL_BORDER_STYLES, BorderStyle } from "../enums/SDKBorderStyle";
import { TokenType } from "../enums/SDKTokenType";
import { ColorToken } from "./SDKColorToken";
import { DimensionToken } from "./SDKDimensionToken";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
export class BorderToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        this.tokenType = TokenType.border;
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    static create(versionId, brandId, name, description, value, alias, referenceResolver, properties, propertyValues) {
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type: TokenType.border,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            // Raw value
            const tokenValue = this.borderValueFromDefinition(value, referenceResolver);
            return new BorderToken(versionId, baseToken, tokenValue, null, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            const tokenValue = {
                color: alias.value.color,
                width: alias.value.width,
                position: alias.value.position,
                style: alias.value.style,
                referencedTokenId: alias.id,
            };
            return new BorderToken(versionId, baseToken, tokenValue, null, properties, propertyValues);
        }
        throw SupernovaError.fromMessage("Border Token must be created using value or alias, but none was provided");
    }
    static borderValueFromDefinition(definition, referenceResolver) {
        let data = definition;
        // For now, handle only one shadow in multiple shadow layers
        if (data instanceof Array) {
            if (data.length > 0) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, prefer-destructuring, @typescript-eslint/prefer-destructuring
                data = data[0];
            }
            else {
                // Empty definition needs to fallback to proper SN definition - make it transparent shadow with 0 0 0 0 values
                data = {
                    color: "rgba(0,0,0,0)",
                    position: "outside",
                    style: "solid",
                    width: 0,
                    type: "border",
                };
            }
        }
        if (!Object.hasOwn(definition, "color") || !Object.hasOwn(definition, "width")) {
            throw SupernovaError.fromMessage(`Border definition is missing one of required properties (color, width), was ${JSON.stringify(definition)}`);
        }
        const value = {}; // Empty container
        const borderDefinition = definition;
        const color = ColorToken.colorValueFromDefinitionOrReference(borderDefinition.color, referenceResolver);
        const width = DimensionToken.dimensionValueFromDefinitionOrReference(borderDefinition.width, referenceResolver, TokenType.borderWidth);
        if (color === undefined) {
            throw new Error(`Unable to resolve value 'color' for border token definition \n${JSON.stringify(definition, null, 2)}\n Did you possibly use incorrect reference?`);
        }
        if (width === undefined) {
            throw new Error(`Unable to resolve value 'width' for border token definition \n${JSON.stringify(definition, null, 2)}\n Did you possibly use incorrect reference?`);
        }
        value.color = color;
        value.position = this.toBorderPosition(borderDefinition.position);
        value.style = this.toBorderStyle(borderDefinition.style);
        value.width = width;
        // TODO: Position, style
        if (value.position === undefined) {
            throw new Error(`Unable to resolve value 'position' for border token definition \n${JSON.stringify(definition, null, 2)}\n Did you possibly use incorrect reference?`);
        }
        return value;
    }
    static toBorderStyle(value) {
        var _a;
        return ((_a = ALL_BORDER_STYLES.find((v) => v.toLowerCase() === (value === null || value === void 0 ? void 0 : value.toLowerCase()))) !== null && _a !== void 0 ? _a : BorderStyle.solid);
    }
    static toBorderPosition(value) {
        var _a;
        return ((_a = ALL_BORDER_POSITIONS.find((v) => v.toLowerCase() === (value === null || value === void 0 ? void 0 : value.toLowerCase()))) !== null && _a !== void 0 ? _a : BorderPosition.outside);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Writing
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = BorderToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a, _b, _c, _d;
        const valueObject = !value.referencedTokenId
            ? {
                color: {
                    aliasTo: (_a = value.color.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
                    value: value.color.referencedTokenId
                        ? null
                        : {
                            color: ColorToken.colorValueToHex8(value.color),
                            opacity: {
                                aliasTo: (_b = value.color.opacity.referencedTokenId) !== null && _b !== void 0 ? _b : undefined,
                                value: value.color.opacity.referencedTokenId
                                    ? null
                                    : {
                                        measure: value.color.opacity.measure,
                                        unit: value.color.opacity.unit,
                                    },
                            },
                        },
                },
                width: {
                    aliasTo: (_c = value.width.referencedTokenId) !== null && _c !== void 0 ? _c : undefined,
                    value: value.width.referencedTokenId
                        ? null
                        : {
                            measure: value.width.measure,
                            unit: value.width.unit,
                        },
                },
                position: value.position,
                style: value.style,
            }
            : undefined;
        return {
            aliasTo: (_d = value.referencedTokenId) !== null && _d !== void 0 ? _d : undefined,
            // @ts-expect-error TS(2322): Type '{ color: { aliasTo: string | undefined; valu... Remove this comment to see the full error message
            value: valueObject,
        };
    }
}
